import { AccountType } from '@src/enums'
import {
    Asset,
    BuyRequest,
    Listing,
    Offer,
    SmAssetRisk,
    TableSorting,
} from '@src/types'
import { AssetExportResponse } from '@utils/api/assets'
import { OrdersResponse } from '@utils/api/orders'
import { AxiosRequestConfig } from 'axios'
import { ApiRes, api } from './client'

export interface PreEmptiveRight {
    account_uid: string | null
    account_type: AccountType
    asset_uid: string | null
    natural_profile_uid: string | null
    legal_profile_uid: string | null
    email: string
    quantity: number
}

export type PreEmptiveRightsResponse = {
    success: boolean
    preEmptiveRights: PreEmptiveRight[]
    preEmptiveRightsCount: number
}

export type ListingsResponse = {
    success: boolean
    listings: Listing[]
    listings_count: number
}

export type BuyRequestsResponse = {
    success: boolean
    buy_requests: BuyRequest[]
    buy_requests_count: number
}

// ------------------------------------------------------------------
// Pre-Emptive Rights Services
// ------------------------------------------------------------------
export const getPreEmptiveRights = (
    filters?: Record<string, unknown>,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<PreEmptiveRightsResponse> => {
    const config: AxiosRequestConfig = {}

    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }
    return api.get('/v2/manage/secondary-market/pre-emptive-rights', config)
}

export const updatePreEmptiveRights = (getPreEmptiveRight: PreEmptiveRight) => {
    return api.post(
        '/v2/manage/secondary-market/update-pre-emptive-rights',
        getPreEmptiveRight
    )
}

// ------------------------------------------------------------------
// Order Book Services
// ------------------------------------------------------------------
export const getOrderBook = (
    filters?: Record<string, unknown>,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<OrdersResponse> => {
    const config: AxiosRequestConfig = {}
    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('v2/manage/secondary-market/order-book', config)
}

// ------------------------------------------------------------------
// Secondary Market Listings Services
// ------------------------------------------------------------------

/**
 * Fetches a list of secondary market listings with the specified filters, pagination, and sorting.
 */
export const getListings = (
    filters?: Record<string, unknown>,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<ListingsResponse> => {
    const config: AxiosRequestConfig = {}
    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('/v2/manage/secondary-market/listings', config)
}
/**
 * Submits new secondary market listing for specific user.
 */
export const createListing = (tempListing: Listing) => {
    return api.post('/v2/manage/secondary-market/listings/store', tempListing)
}

/**
 * Cancels a secondary market listing by ID.
 */
export const cancelListing = (id: string): ApiRes<{ success: boolean }> => {
    return api.delete(`/v2/manage/secondary-market/listings/${id}`)
}

/**
 * Get SM enabled Assets for Platform
 */
export const getSmAssets = (platformName: string) => {
    return api.get(`/v2/manage/secondary-market/${platformName}/assets`)
}

// ------------------------------------------------------------------
// Secondary Market Buy Request Services
// ------------------------------------------------------------------

/**
 * Fetches a list of secondary market listings with the specified filters, pagination, and sorting.
 */
export const getBuyRequests = (
    filters?: Record<string, unknown>,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<BuyRequestsResponse> => {
    const config: AxiosRequestConfig = {}
    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('/v2/manage/secondary-market/buy-requests', config)
}

/**
 * Submits new secondary market buy request for specific user.
 */
export const createBuyRequest = (tempBuyRequest: BuyRequest) => {
    return api.post(
        '/v2/manage/secondary-market/buy-requests/store',
        tempBuyRequest
    )
}

/**
 * Cancels a secondary market buy request by ID.
 */
export const cancelBuyRequest = (id: string): ApiRes<{ success: boolean }> => {
    return api.delete(`/v2/manage/secondary-market/buy-requests/${id}`)
}

/**
 * Regenerates an offer by ID.
 */
export const regenerateOffer = (id: string): ApiRes<{ success: boolean }> => {
    return api.post(`/v2/manage/secondary-market/offers/${id}/regenerate`)
}

// ------------------------------------------------------------------
// Secondary Market Offer Types
// ------------------------------------------------------------------

export type OffersResponse = {
    success: boolean
    offers: Offer[]
    offers_count: number
}

// ------------------------------------------------------------------
// Secondary Market Offer Services
// ------------------------------------------------------------------

/**
 * Fetches a list of secondary market offers with the specified filters, pagination, and sorting.
 */
export const getOffers = (
    filters?: Record<string, unknown>,
    page?: number,
    limit?: number,
    sorting?: TableSorting[]
): ApiRes<OffersResponse> => {
    const config: AxiosRequestConfig = {}
    config.params = {}

    if (filters && Object.keys(filters).length) {
        config.params = filters
    }

    if (typeof page === 'number' && typeof limit === 'number') {
        config.params['range'] = page * limit - limit + '-' + page * limit
    }

    if (sorting && sorting.length) {
        config.params['sort'] = sorting
            .map((sort) => {
                return (sort.direction === 'desc' ? '-' : '') + sort.field
            })
            .join(',')
    }

    return api.get('/v2/manage/secondary-market/offers', config)
}

/**
 * Fetches a list of secondary market offers with the specified filters, pagination, and sorting.
 */
export const getOffer = (id: string): ApiRes<any> => {
    return api.get(`/v2/manage/secondary-market/offers/${id}`)
}

/**
 * Submits a new offer for a listing or buy request.
 */
export const createOffer = (tempOffer: Offer) => {
    return api.post('/v2/manage/secondary-market/offers/store', tempOffer)
}

/**
 * Manually (re-)sends a lifecycle email for an offer/transaction.
 */
export const sendOfferEmail = (
    offerId: string,
    type: string,
    recipientRole?: string
): ApiRes<{ success: boolean; message: string }> => {
    return api.post(
        `/v2/manage/secondary-market/offers/${offerId}/send-email`,
        {
            type,
            recipient_role: recipientRole,
        }
    )
}

// ------------------------------------------------------------------
// Secondary Market Asset Risk Types & Services
// ------------------------------------------------------------------

export type AssetRisksResponse = {
    data: SmAssetRisk[]
}

export type SyncAssetRisksResponse = {
    message: string
    data: SmAssetRisk[]
}

/**
 * Fetches all secondary market risks for a given asset.
 */
export const getAssetRisks = (assetUid: string): ApiRes<AssetRisksResponse> => {
    return api.get(`/v2/manage/secondary-market/assets/${assetUid}/risks`)
}

/**
 * Batch syncs (create, update, delete, reorder) secondary market risks for an asset.
 */
export const syncAssetRisks = (
    assetUid: string,
    risks: SmAssetRisk[]
): ApiRes<SyncAssetRisksResponse> => {
    return api.post(
        `/v2/manage/secondary-market/assets/${assetUid}/risks/sync`,
        {
            risks,
        }
    )
}

/**
 * Export Secondary Market Asset Pre-Emptive Rights
 */
export const exportSecondaryMarketAssetPreEmptiveRights = (
    asset: Asset
): ApiRes<AssetExportResponse> =>
    api.post(
        'v2/manage/assets/' + asset.asset_uid + '/export-pre-emptive-rights',
        { lang: 'en', async: 1 }
    )
